import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Autocomplete, Backdrop, Box, Button, CircularProgress, Divider, Grid, Paper, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import Message from "../../components/Message";
import ConfirmDialog from "../../components/ConfirmDialog";
import { getAllLocations, getAllStoreSkus } from "../../controllers/CatalogController";
import { flattenLocationStores } from "../../utils/locations";
import { getSortOrder, updateSortOrder } from "../../controllers/SortOrderController";
import SortOrderList from "./components/SortOrderList";
import AddProductPanel from "./components/AddProductPanel";

// Normalizes both the store-SKU list and the sort-order response into the
// same {id, name} shape the list/search UI works with. productItemId/name
// match the fields already used for SKUs elsewhere (see DiscountContainer).
const normalizeSku = (sku) => ({
    id: String(sku.productItemId ?? sku.id),
    name: sku.name,
});

export default function SortOrderContainer() {
    const [searchParams, setSearchParams] = useSearchParams();
    const storeId = searchParams.get("storeId") || "";

    const [stores, setStores] = useState([]);
    const [items, setItems] = useState([]);
    const [skuOptions, setSkuOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
    const [apiMessage, setApiMessage] = useState({ open: false, type: "success", message: "" });
    const [removeConfirm, setRemoveConfirm] = useState({ open: false, item: null });
    const [newIds, setNewIds] = useState(new Set());
    const [movedIds, setMovedIds] = useState(new Set());

    const selectedIds = useMemo(() => new Set(items.map((item) => item.id)), [items]);
    const changedIds = useMemo(() => new Set([...newIds, ...movedIds]), [newIds, movedIds]);

    useEffect(() => {
        fetchStores();
    }, []);

    // The sort-order endpoint only returns productItemId, no name - backfill
    // names once the store's SKU catalog (which has both) is loaded.
    useEffect(() => {
        if (!skuOptions.length) return;
        setItems((prev) => {
            let changed = false;
            const next = prev.map((item) => {
                if (item.name) return item;
                const match = skuOptions.find((sku) => sku.id === item.id);
                if (!match?.name) return item;
                changed = true;
                return { ...item, name: match.name };
            });
            return changed ? next : prev;
        });
    }, [skuOptions]);

    useEffect(() => {
        setIsAddPanelOpen(false);
        setNewIds(new Set());
        setMovedIds(new Set());
        if (!storeId) {
            setItems([]);
            setSkuOptions([]);
            return;
        }
        loadSortOrder(storeId);
        loadStoreSkus(storeId);
    }, [storeId]);

    const fetchStores = async () => {
        try {
            const response = await getAllLocations();
            setStores(flattenLocationStores(response, { withCatalogId: false }));
        } catch (error) {
            setApiMessage({ open: true, type: "error", message: error?.message || "Failed to load stores" });
        }
    };

    const loadSortOrder = async (id) => {
        try {
            setLoading(true);
            const response = await getSortOrder(id);
            setItems((response || []).map(normalizeSku));
        } catch (error) {
            setItems([]);
            setApiMessage({ open: true, type: "error", message: error?.message || "Failed to load sort order" });
        } finally {
            setLoading(false);
        }
    };

    const loadStoreSkus = async (id) => {
        try {
            const response = await getAllStoreSkus(id);
            setSkuOptions((response || []).map(normalizeSku));
        } catch (error) {
            setSkuOptions([]);
            setApiMessage({ open: true, type: "error", message: error?.message || "Failed to load SKUs" });
        }
    };

    const handleStoreChange = (selectedStore) => {
        const params = new URLSearchParams(searchParams);
        if (selectedStore) {
            params.set("storeId", selectedStore.storeId);
        } else {
            params.delete("storeId");
        }
        setSearchParams(params);
    };

    const removeItem = (id) => {
        setItems((prev) => prev.filter((item) => item.id !== id));
        setNewIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
        setMovedIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    };

    const handleReorder = (nextItems, movedId) => {
        setItems(nextItems);
        if (movedId == null) return;
        setMovedIds((prev) => (prev.has(movedId) ? prev : new Set(prev).add(movedId)));
    };

    const handleRemoveRequest = (id) => {
        // Not yet saved - remove immediately, nothing to lose confirming.
        if (newIds.has(id)) {
            removeItem(id);
            return;
        }
        const item = items.find((item) => item.id === id);
        setRemoveConfirm({ open: true, item });
    };

    const handleConfirmRemove = () => {
        removeItem(removeConfirm.item?.id);
        setRemoveConfirm({ open: false, item: null });
    };

    const handleToggleProduct = (option) => {
        const alreadyAdded = items.some((item) => item.id === option.id);

        setItems((prev) =>
            alreadyAdded
                ? prev.filter((item) => item.id !== option.id)
                : [...prev, option]
        );

        setNewIds((prev) => {
            const next = new Set(prev);
            if (alreadyAdded) {
                next.delete(option.id);
            } else {
                next.add(option.id);
            }
            return next;
        });
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const payload = items.map((item, index) => ({
                productItemId: Number(item.id),
                sortOrder: index + 1,
            }));
            await updateSortOrder(storeId, payload);
            setApiMessage({ open: true, type: "success", message: "Sort order saved" });
            setNewIds(new Set());
            setMovedIds(new Set());
        } catch (error) {
            setApiMessage({ open: true, type: "error", message: error?.message || "Failed to save sort order" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Flash Sale - Product Sort Order
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2} sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Grid item size={{ xs: 12, md: 4 }}>
                        <Autocomplete
                            fullWidth
                            size="small"
                            options={stores}
                            value={stores.find((s) => String(s.storeId) === String(storeId)) || null}
                            isOptionEqualToValue={(option, selected) => String(option.storeId) === String(selected.storeId)}
                            getOptionLabel={(option) => `${option?.storeId} - ${option?.storeName || ""}`}
                            onChange={(_, selectedStore) => handleStoreChange(selectedStore)}
                            renderInput={(params) => <TextField {...params} label="Store" />}
                        />
                    </Grid>

                    <Grid item size={{ xs: 6, md: 6 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                        {!isAddPanelOpen && <Button
                            variant="outlined"
                            startIcon={isAddPanelOpen ? <CloseIcon /> : <AddIcon />}
                            disabled={!storeId}
                            onClick={() => setIsAddPanelOpen((prev) => !prev)}
                            style={{ marginRight: '10px' }}
                        >
                            Add Product
                        </Button>}
                        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={!storeId || saving}>
                            Save Sort Order
                        </Button>
                    </Grid>

                </Grid>
            </Paper>

            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <SortOrderList items={items} onReorder={handleReorder} onRemove={handleRemoveRequest} changedIds={changedIds} />
                </Box>

                {isAddPanelOpen && (
                    <AddProductPanel
                        options={skuOptions}
                        selectedIds={selectedIds}
                        onToggle={handleToggleProduct}
                        onClose={() => setIsAddPanelOpen(false)}
                    />
                )}
            </Box>


            <Backdrop open={loading} sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <Message
                open={apiMessage.open}
                type={apiMessage.type}
                message={apiMessage.message}
                onClose={() => setApiMessage((prev) => ({ ...prev, open: false }))}
            />

            <ConfirmDialog
                open={removeConfirm.open}
                title="Remove Product"
                message={`Are you sure you want to remove "${removeConfirm.item?.name}" (#${removeConfirm.item?.id}) from the list?`}
                confirmText="Remove"
                confirmColor="error"
                onClose={() => setRemoveConfirm({ open: false, item: null })}
                onConfirm={handleConfirmRemove}
            />
        </Box>
    );
}
